import { EventsFilterParams } from "@classes/StreamEvents/EventsFilter";
import { isArray, isPlainObject, isString, uniq } from "lodash";

/**
 * Helpers for the company/site selection of the Global Event Inbox in multi-company mode (no company selected).
 * None of these are used when a company is selected.
 *
 * Selection rules (enforced by normalizeMultiCompanySelection):
 * - `companies` holds every company with at least one selected site.
 * - `companySites` only has an entry for a partially selected company. No entry means all sites of that company.
 * - A company never stays selected with zero sites.
 */

const STORAGE_KEY = "overview_multicompany_selection";

export type MultiCompanySelection = {
  companies: string[];
  companySites: Record<string, string[]>;
}

export function normalizeMultiCompanySelection(
  selection: MultiCompanySelection,
  sitesByCompany: Map<string, Site[]>
): MultiCompanySelection {
  const companies: string[] = [];
  const companySites: Record<string, string[]> = {};

  uniq(selection.companies).forEach((companyId) => {
    const restriction = selection.companySites[companyId];

    if (restriction === undefined) {
      companies.push(companyId);
      return;
    }

    let siteIds = uniq(restriction);
    const loadedSites = sitesByCompany.get(companyId);

    // Only prune against the site list when it is known, otherwise keep the stored restriction as is.
    if (loadedSites && loadedSites.length > 0) {
      const loadedSiteIds = new Set(loadedSites.map(el => el.siteId));
      siteIds = siteIds.filter(el => loadedSiteIds.has(el));

      if (siteIds.length === loadedSiteIds.size) {
        companies.push(companyId);
        return;
      }
    }

    if (siteIds.length === 0) return;

    companies.push(companyId);
    companySites[companyId] = siteIds;
  })

  return { companies, companySites };
}

/**
 * Returns the filters to use on the inbox API request of a given company,
 * or null when the company must not be requested at all.
 */
export function buildCompanyRequestFilters(filters: EventsFilterParams | undefined, companyId: string): EventsFilterParams | undefined | null {
  const restriction = filters?.companySites?.[companyId];
  if (filters === undefined || restriction === undefined) return filters;

  // Never send an empty site list, the API could interpret it as "no site filtering".
  if (restriction.length === 0) return null;

  return { ...filters, sites: { sites: restriction } };
}

export function saveMultiCompanySelection(selection: MultiCompanySelection): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
  } catch (e) {
    console.log(e);
    console.log('Error writing to LocalStorage');
  }
}

export function loadMultiCompanySelection(): MultiCompanySelection | undefined {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (!value) return undefined;

    const json = JSON.parse(value);
    if (!isPlainObject(json) || !isArray(json.companies) || !isPlainObject(json.companySites)) return undefined;

    const companies: string[] = json.companies.filter(isString);
    const companySites: Record<string, string[]> = {};

    Object.entries(json.companySites as Record<string, unknown>).forEach(([companyId, siteIds]) => {
      if (isArray(siteIds)) companySites[companyId] = siteIds.filter(isString);
    })

    return { companies, companySites };
  } catch (e) {
    console.log(e);
    return undefined;
  }
}
